import { AccountInfoResult } from './AccountInfo';
import { PaginatedResponse } from './Deploy';
import { ValidatorAveragePerformanceResult } from './ValidatorAveragePerformance';

export enum AuctionManagerEntryPoint {
  delegate = 'delegate',
  undelegate = 'undelegate',
  redelegate = 'redelegate',
  add = 'add_bid',
  withdraw = 'withdraw_bid',
  activate = 'activate_bid',
}

export type ResponseError = { message?: string };

export type DataResponse<T = any> = {
  data: T;
  error?: ResponseError;
};

export interface Validator {
  key: string;
  delegationFee: number;
  delegatorStake: number;
  delegatorCount: number;
  active: boolean;
  selfStakeAmount: number;
  bidAmount: number;
  totalStakeAmount: number;
  selfStakePercentage: string;
  rank: number;
  era_id: number;
  totalNetworkStakePercentage: string;
  accountInfo?: AccountInfoResult;
  centralizedAccountInfo?: AccountInfoResult | null;
  validatorAccountInfo?: AccountInfoResult | null;
  averagePerformance?: ValidatorAveragePerformanceResult;
  csprName?: string | null;
  maximumDelegationAmount: string;
  minimumDelegationAmount: string;
  reservedSlots: number;
}

export interface ValidatorResult {
  account_info?: AccountInfoResult;
  average_performance?: ValidatorAveragePerformanceResult;
  centralized_account_info?: AccountInfoResult | null;
  validator_account_info?: AccountInfoResult | null;
  delegators_number: number;
  delegators_stake: number;
  era_id: number;
  fee: number;
  is_active: boolean;
  network_share: string;
  public_key: string;
  rank: number;
  self_share: string;
  self_stake: number;
  bid_amount: number;
  total_stake: number;
  cspr_name?: string | null;
  maximum_delegation_amount: string;
  minimum_delegation_amount: string;
  reserved_slots: number;
}

export interface DelegatorBidderResult {
  delegators_number: number;
  delegators_stake: number;
  era_id: number;
  fee: number;
  is_active: boolean;
  network_share: string;
  public_key: string;
  rank: number;
  self_share: string;
  self_stake: number;
  bid_amount: number;
  total_stake: number;
}

export interface ValidatorWithInfo extends Validator {
  isVerifiedValidator?: boolean;
}

export interface ValidatorMetricsResult {
  total_active_era_stake: string;
  active_validator_number: number;
  current_era_id: number;
  total_bids_number: number;
  active_bids_number: number;
}

export const MapValidators = ({
  fee,
  is_active,
  self_stake,
  total_stake,
  self_share,
  delegators_number,
  network_share,
  delegators_stake,
  public_key,
  account_info,
  average_performance,
  centralized_account_info,
  validator_account_info,
  bid_amount,
  cspr_name,
  maximum_delegation_amount,
  minimum_delegation_amount,
  reserved_slots,
  ...rest
}: ValidatorResult): Validator => ({
  ...rest,
  delegationFee: fee,
  delegatorCount: delegators_number,
  active: is_active,
  selfStakeAmount: self_stake,
  bidAmount: bid_amount,
  totalStakeAmount: total_stake,
  selfStakePercentage: self_share,
  totalNetworkStakePercentage: network_share,
  delegatorStake: delegators_stake,
  key: public_key,
  accountInfo: account_info,
  centralizedAccountInfo: centralized_account_info,
  validatorAccountInfo: validator_account_info,
  averagePerformance: average_performance,
  csprName: cspr_name,
  maximumDelegationAmount: maximum_delegation_amount,
  minimumDelegationAmount: minimum_delegation_amount,
  reservedSlots: reserved_slots,
});

export const MapPaginatedValidators = ({
  data,
  ...rest
}: PaginatedResponse<ValidatorResult>): PaginatedResponse<Validator> => ({
  ...rest,
  data: data ? data.map(MapValidators) : [],
});

export const MapDataResponseValidators = (
  args: DataResponse<ValidatorResult>
): DataResponse<Validator> => {
  if (args.error) {
    return { 
      data: args.data ? MapValidators(args.data) : undefined, 
      error: args.error 
    } as DataResponse<Validator>;
  }

  return {
    ...args,
    data: MapValidators(args.data),
  };
};
